import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { operationStatusView } from '../operationStatus'
import { operationCodeAndName } from '../operationNameLabel'
import { operationWorkerLabel, operationWorkersLabel } from '../operationWorkerLabels'
import { formatDuration } from '../duration'
import { raisedColor, sunkenColor } from '../detailSurfaces'
import { openIncidences } from '../operationRowFlags'
import OperationStatusCell from './OperationStatusCell'
import ExpandRowButton from './ExpandRowButton'
import OperationExpansionDetails from './OperationExpansionDetails'
import PlanningGroupCell from './PlanningGroupCell'
import { NUMERIC_FONT } from './operationRowCells'
import {
  ACTIONS_COLUMN, ACTIONS_COLUMN_WIDTH, ELASTIC_OPERATION_COLUMN, MACHINE_COLUMN, PLANNED_WORKER_COLUMN,
  PLANNED_WORKERS_SHOWN, THEORETICAL_TIME_COLUMN, stickyColumn
} from './operationColumns'
import { isBlank } from '../detailShared'

// ═══ LIENZO «Flujo de trabajo» (Workflow.dc.html) — LA FASE TIÑE LA FILA ═══
//
// El lienzo separa tres aspectos de fila: la que está EN CURSO se levanta con un velo teal, la que
// AÚN NO HA ARRANCADO se hunde y se apaga (`--mic-surface-off`, texto `--mic-text-2`), y el resto
// se queda como está. Es el punto 2.1 del handoff aplicado a la lista: la fila entera repite lo que
// ya dicen el icono y el rótulo de la píldora, nunca lo sustituye (RN35).
//
// ⚠️ NO SE CABLEA DESDE `row.status`: se deriva del MISMO `iconKey` del que salen el icono y el
// color, para que un estado nuevo no pueda entrar por una puerta y quedarse fuera de la otra. Un
// valor desconocido cae a `''` —sin tinte—, que es la misma cautela de `operationStatusView`: un
// servidor que no reconocemos se ve antes callando que inventando.
//
// ⚠️⚠️ `interrupted` NO LLEVA EL TINTE ROJO QUE LE PONE EL LIENZO, y es deliberado. Aquí el rojo de
// fila significa UNA cosa —hay incidencias abiertas, y la píldora dice cuántas—; el dibujo lo usa
// además para una operación parada, donde el dato no afirma ningún bloqueo. Dos significados para
// el mismo rojo dejarían de significar cualquiera de los dos. La parada se lee en su píldora.
const STATUS_ROW_TONE = {
  finished: '',
  cancelled: '',
  interrupted: '',
  'active-execution': 'active',
  'active-preparation': 'active',
  waiting: 'idle',
  pending: 'idle',
  'not-available': 'idle'
}

// ONE operation: its row and, when open, its detail row. Memoized: the table re-renders on every key
// of the search box and on every toggle, and a large order has hundreds of rows — a row only renders
// again when what it shows changes, so every prop is a value or a stable callback.
const OperationRow = React.memo(({
  row,
  fabricationOrderId,
  balancedView,
  expanded,
  expansion,
  theoretical,
  // The visible width of the table, only for an open row (a closed one must not render on a resize).
  expandedWidth,
  editingGroup,
  canEditGroup,
  usedPlanningGroups,
  onToggle,
  onRetry,
  onStartEditGroup,
  onStopEditGroup,
  onGroupSaved
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { formatDate, formatDateTime, formatTime } = useDateFormatter()
  // The pinned pair: the group cell also carries the divider that tells the reader the columns to
  // its left are sliding underneath it.
  const stickyActionsColumn = stickyColumn(0, theme)
  const stickyGroupColumn = {
    ...stickyColumn(ACTIONS_COLUMN_WIDTH, theme),
    borderLeft: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
  }

  const statusView = operationStatusView(row.status)
  const notScheduled = isBlank(row.plannedStartDate) || isBlank(row.plannedEndDate)
  // Defecto 1 (pasada 21): start/end on the same day collapse to one line — the date
  // said once plus both times — instead of repeating the date twice and wrapping.
  const sameDay = !notScheduled && formatDate(row.plannedStartDate) === formatDate(row.plannedEndDate)
  // ADDENDUM 20 (E26): la identificación sale de la MISMA función que usa la cabecera,
  // no de leer `row.code` a pelo. Que las dos coincidan tiene que ser por construcción
  // y no por cuidado: eran dos sitios derivando la misma regla por su cuenta, y esta
  // regla acaba de cambiar una vez.
  const { identifier: rowIdentifier } = operationCodeAndName(row)
  // ADDENDUM 44 (E50)
  const incidencias = openIncidences(row)
  // Lienzo: la fase de la fila sale del MISMO `iconKey` que el icono y el color.
  const rowTone = STATUS_ROW_TONE[statusView.iconKey] || ''
  // ⚠️⚠️ LA LÍNEA PEQUEÑA SOLO SE ESCRIBE CUANDO DICE ALGO DISTINTO DE LA GRANDE, y
  // la comparación es contra el TEXTO, no contra "¿hay nombre?". Eran dos columnas y
  // ahí repetir era tolerable —el PO lo aceptó en S333 porque se corrige solo según
  // lleguen nombres—; apiladas en la MISMA celda serían el mismo texto dos veces,
  // una encima de otra, que ya no es tolerable sino ruido.
  //
  // ⚠️ Mi primera versión preguntaba `row.name ? ...`, y con eso el caso de S344
  // —SIN código y CON nombre, donde `operationCodeAndName` devuelve el nombre como
  // identificador— volvía a duplicar. Ese test estaba en verde porque afirma que el
  // texto ESTÁ, no que esté una sola vez: comparar los textos cubre las cuatro
  // combinaciones sin enumerarlas.
  // S424 — los nombres que SE PUEDEN escribir, con la misma función que los escribe en el
  // recorrido de la pieza; el filtro solo tira los que no identifican a nadie.
  const plannedNames = (row.plannedWorkers || []).map(operationWorkerLabel).filter((workerName) => workerName !== '')
  const lineaNombre = row.name || row.code
  const lineaIdentificador = [
    rowIdentifier && rowIdentifier !== lineaNombre ? rowIdentifier : '',
    isBlank(row.jobOrder) ? '' : t_('OP {{order}}', { order: row.jobOrder }),
    // In the balanced view the row says the position that places it, or that it has none.
    !balancedView ? '' : isBlank(row.linePosition) ? t_('Unassigned') : t_('pos. {{position}}', { position: row.linePosition })
  ].filter((parte) => parte !== '').join(' · ')

  return (
    <>
      <TableRow
        data-testid={`operation-row-${row.jobReferenceId}`}
        data-tone={rowTone || 'plain'}
        hover
        sx={{
          // ⚠️ S442 (D13): el estado ya no se repite en ninguna barra de color al
          // borde de la fila; lo dice la píldora, con palabra e icono (RN35).
          //
          // Tinte de fase (lienzo): la que está en curso se levanta, la que aún no ha
          // arrancado se hunde. Nada de esto dice por sí solo en qué estado está la
          // operación: eso sigue siendo trabajo de la píldora.
          ...(rowTone === 'active' ? { backgroundColor: alpha(theme.palette.info.main, 0.05) } : {}),
          ...(rowTone === 'idle' ? { backgroundColor: sunkenColor(theme) } : {}),
          // ⚠️ ADDENDUM 44 — el mockup rodea ENTERA la fila con incidencias. Se hace
          // por arriba, abajo y derecha, NUNCA por la izquierda: ese borde es del
          // ESTADO y tiene dueño. Robárselo dejaría a una operación con incidencias
          // sin decir en qué estado está, que es el canal que RN35 protege.
          // Y el recuadro no es la única señal: la píldora roja dice cuántas son.
          // Va DESPUÉS del tinte de fase por la prioridad del §2.1 (Bloqueo primero).
          ...(incidencias >= 1
            ? {
                backgroundColor: alpha(theme.palette.error.main, 0.05),
                '& > td': { borderTop: `1px solid ${alpha(theme.palette.error.main, 0.45)}`, borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.45)}` },
                '& > td:last-of-type': { borderRight: `1px solid ${alpha(theme.palette.error.main, 0.45)}` }
              }
            : {})
        }}
      >
        <TableCell sx={{ ...ELASTIC_OPERATION_COLUMN, pl: 1.25 }}>
          <Stack direction='row' spacing={1.25} alignItems='stretch'>
            {/* S442 (D13) — aquí iba la barra de color del estado, y se quitó: el
                estado ya lo dice, con palabra e icono, la píldora de su columna. */}
            <Box sx={{ minWidth: 0 }}>
              {/* ADDENDUM 20 (E26): el que HAYA, no siempre el código. Sin código —el
                  caso que destapó materiales— esta celda se quedaba vacía teniendo un
                  nombre que la identifica perfectamente.
                  ⚠️ El testid se queda aunque la línea pueda no pintarse: quien lo busque
                  tiene que poder distinguir "no hay identificador que añadir" de "la
                  celda se rompió", y para eso el nodo existe y está vacío.
                  §8: el CÓDIGO NUNCA SE TRUNCA —es lo que identifica la fila—, así que
                  aquí no hay `ellipsis`; el que puede recortarse es el nombre. */}
              <Typography data-testid='operation-code' variant='caption' sx={{ display: 'block', ...NUMERIC_FONT, fontSize: 11, letterSpacing: '0.06em', color: 'text.secondary', lineHeight: 1.4 }}>
                {lineaIdentificador}
              </Typography>
              {/* S333: el reparto en pantalla es por COLUMNAS, no por celda. `CODE` no
                  cambia nunca; `JOB` muestra el nombre cuando existe y el código mientras
                  no exista. Hoy eso repite el mismo texto en 2.906 filas, y el PO lo acepta
                  a sabiendas por una razón que las otras salidas no tienen: SE CORRIGE
                  SOLA. Según los clientes vayan rellenando nombres, cada fila deja de
                  repetirse sin que nadie toque nada — la pantalla mejora con el dato, no
                  con una versión nuestra. Un hueco o un aviso repetido no harían eso.
                  Nombre y código idénticos caen aquí en el mismo sitio: mostrar el nombre
                  es mostrar ese valor, así que la rama 2 no necesita trato aparte en la
                  tabla (sí en la cabecera, donde van juntos). */}
              {/* §2.4: el nombre en *sentence case* —lo pone el dato, aquí solo se evita
                  forzar versalitas— y a peso 500, no 700: el rótulo grande de la fila no
                  tiene que gritar más que la píldora que dice si algo va mal.
                  §8: UNA línea con `ellipsis` y el `title` entero. Un nombre recortado sin
                  forma de leerlo completo sería una ausencia encubierta (RN8). */}
              <Typography
                data-testid='operation-name'
                variant='body2'
                title={lineaNombre || ''}
                sx={{ fontSize: 14.5, fontWeight: 500, color: rowTone === 'idle' ? 'text.secondary' : 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {lineaNombre}
              </Typography>
              {/* ⚠️ RN8 — Sun modela la tecnología como OPCIONAL y esto la pintaba como cadena
                  vacía: un renglón en blanco bajo el nombre de la operación, que no dice
                  «no tiene tecnología», no dice nada, y se lee como un dato que no ha
                  llegado. Se declara con el mismo guion y la misma marca `data-absent`
                  que las cifras de la celda de cantidades, para que un test distinga
                  «no llegó» de «llegó» sin tener que leer el color. */}
              <Typography
                data-testid='operation-technology'
                data-absent={isBlank(row.technology?.name) ? 'true' : 'false'} // system-value: raw-ok
                variant='caption'
                sx={{ display: 'block', color: isBlank(row.technology?.name) ? 'text.disabled' : 'text.secondary' }} // system-value: raw-ok
              >
                {isBlank(row.technology?.name) ? '—' : systemValueLabel(t_, 'technology', row.technology)}
              </Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          {/* S444 — la celda de ESTADO vive en `OperationStatusCell`, compartida con la
              fila de Progress: un rótulo o un color que cambie, cambia en las dos. */}
          <OperationStatusCell row={row} />
        </TableCell>
        <TableCell sx={THEORETICAL_TIME_COLUMN}>
          {/* S423 — el teórico de la operación, que N3 ya sirve (S425: el desplegable no lo
              repite como total). Ausente se dice; un cero es un cero. */}
          <Typography
            data-testid='operation-theoretical-time'
            data-absent={isBlank(row.theoreticalDurationSeconds) ? 'true' : 'false'}
            variant='body2'
            sx={{ ...NUMERIC_FONT, whiteSpace: 'nowrap', color: isBlank(row.theoreticalDurationSeconds) ? 'text.disabled' : 'text.primary' }}
          >
            {isBlank(row.theoreticalDurationSeconds) ? '—' : formatDuration(row.theoreticalDurationSeconds)}
          </Typography>
        </TableCell>
        <TableCell sx={PLANNED_WORKER_COLUMN}>
          {/* ⚠️⚠️ S424 — SIEMPRE VISIBLE, y con TRES respuestas, no dos (el porqué, en
              `mapOperationDto`): el campo no llega → «No informado por el servidor»;
              llega vacío → «Sin planificar»; llega con gente → sus nombres. En la base
              del PO saldrá «Sin planificar» en TODAS las filas, y es verdad.
              📌 Dos nombres y el resto contado, igual que la columna que ocupaba este
              sitio (D11): apilados, y partidos en líneas si no caben; el corte se DICE (RN8).
              El `title` lleva la lista completa. */}
          <Box
            data-testid='operation-planned-worker'
            data-absent={!row.plannedWorkersServed ? 'server' : row.plannedWorkers.length === 0 ? 'none' : 'false'}
            title={operationWorkersLabel(row.plannedWorkers)}
            sx={{ width: '100%' }}
          >
            {!row.plannedWorkersServed && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('Not reported by the server')}</Typography>
            )}
            {row.plannedWorkersServed && plannedNames.length === 0 && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('No planned worker')}</Typography>
            )}
            {plannedNames.slice(0, PLANNED_WORKERS_SHOWN).map((workerName, position) => (
              <Typography
                // La posición entra en la clave: dos operarios pueden llegar con el
                // mismo nombre, y ahí el nombre solo no distingue.
                key={`${position}-${workerName}`}
                data-testid='operation-planned-worker-name'
                variant='body2'
                // S423: parte en líneas, nunca «…» — un nombre recortado es otro nombre.
                sx={{ overflowWrap: 'anywhere' }}
              >
                {workerName}
              </Typography>
            ))}
            {plannedNames.length > PLANNED_WORKERS_SHOWN && (
              <Typography data-testid='operation-planned-workers-more' variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                {t_('+{{count}} more', { count: plannedNames.length - PLANNED_WORKERS_SHOWN })}
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {/* Defecto 1 (pasada 21): a compact, non-wrapping range — stacked start/end
              when they fall on different days (start on top, end below in
              text.secondary), a single line with one date and both times when they
              fall on the same day (§ mockup: "08/14/2026 12:05 – 12:06"). */}
          {/* §2.4 — las fechas y las horas van en la fuente de números, que es lo que
              hace que esta columna se lea de arriba abajo como una columna y no como
              seis cadenas sueltas. La AUSENCIA no: «sin planificar» es texto de
              interfaz, no un dato, y ponerlo en mono lo disfrazaría de valor. */}
          <Box data-testid='operation-planned-dates'>
            {notScheduled && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('Not scheduled')}</Typography>
            )}
            {!notScheduled && sameDay && (
              <Stack spacing={0}>
                <Typography variant='body2' sx={{ ...NUMERIC_FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{formatDate(row.plannedStartDate)}</Typography>
                {/* Defecto 8 (pasada 33): cuando inicio y fin coinciden, el rango era
                    "12:08 – 12:08", que hace leer dos veces para descubrir que no hay
                    duración. Una sola hora lo dice a la primera. */}
                <Typography variant='body2' sx={{ ...NUMERIC_FONT, fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {formatTime(row.plannedStartDate) === formatTime(row.plannedEndDate)
                    ? formatTime(row.plannedStartDate)
                    : `${formatTime(row.plannedStartDate)} – ${formatTime(row.plannedEndDate)}`}
                </Typography>
              </Stack>
            )}
            {!notScheduled && !sameDay && (
              <Stack spacing={0}>
                <Typography variant='body2' sx={{ ...NUMERIC_FONT, fontSize: 12.5, whiteSpace: 'nowrap' }}>{formatDateTime(row.plannedStartDate)}</Typography>
                <Typography variant='body2' sx={{ ...NUMERIC_FONT, fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatDateTime(row.plannedEndDate)}</Typography>
              </Stack>
            )}
          </Box>
        </TableCell>
        <TableCell sx={MACHINE_COLUMN}>
          <Typography
            data-testid='operation-device'
            variant='body2'
            title={row.device?.name || ''}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
          >
            {row.device?.name || (notScheduled ? t_('Not scheduled') : t_('Not reported'))}
          </Typography>
        </TableCell>
        <TableCell sx={stickyGroupColumn}>
          <PlanningGroupCell
            row={row}
            fabricationOrderId={fabricationOrderId}
            editing={editingGroup}
            canEdit={canEditGroup}
            usedPlanningGroups={usedPlanningGroups}
            onStartEdit={onStartEditGroup}
            onStopEdit={onStopEditGroup}
            onSaved={onGroupSaved}
          />
        </TableCell>
        <TableCell sx={{ ...ACTIONS_COLUMN, ...stickyActionsColumn }}>
          {/* ⚠️ ERA UN BOTÓN DE TEXTO («Details») Y AHORA ES UN CHEVRON, que es lo
              que pide §2.5 con su medida: 44×44, el mínimo táctil. Un icono solo se
              queda sin nombre para quien no lo ve, así que lleva `aria-label` Y
              `title` con la operación concreta — no "Abrir detalle" a secas, que en
              una tabla de cuarenta filas no dice cuál. El `data-testid` no cambia:
              lo que hace el botón es lo mismo. */}
          {/* S459 — EL MISMO botón que ahora usa Progress: `ExpandRowButton`, único
              componente del que tiran las dos tablas. El testid no cambia: lo que
              hace el botón es lo mismo. */}
          <ExpandRowButton
            testId={`expand-operation-${row.jobReferenceId}`}
            ariaLabel={t_('Open operation detail {{operation}}', { operation: rowIdentifier })}
            expanded={expanded}
            onClick={() => onToggle(row.jobReferenceId)}
          />
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell
            // ⚠️ OCHO desde S423: código y nombre en una celda (addendum 44), y las
            // cifras y el tiempo real se fueron a Progress (−2) mientras entraba el
            // tiempo teórico (+1). Un `colSpan` corto no rompe nada
            // visible de golpe —deja el desplegable sin cubrir la última columna y
            // el borde se descuadra—, así que es de los que se quedan mal meses.
            colSpan={8}
            data-testid={`operation-expansion-${row.jobReferenceId}`}
            sx={{
              backgroundColor: raisedColor(theme),
              borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
              // El relleno pasa a la caja de dentro: si se quedara aquí, la caja pegada
              // arrancaría desplazada por él y volvería a sobresalir por la derecha.
              p: 0
            }}
          >
            {/* `sticky` + `left: 0` es el MISMO mecanismo que ya fija las dos columnas
                de la derecha en esta tabla (`stickyColumn`), no uno nuevo. Además de no
                recortarse, gana algo mejor: el detalle se queda a la vista mientras la
                tabla se desplaza por debajo, en vez de irse de la pantalla. */}
            <Box
              data-testid={`operation-expansion-viewport-${row.jobReferenceId}`}
              sx={{
                position: 'sticky',
                left: 0,
                width: expandedWidth || 'auto',
                px: 2,
                py: 0.75
              }}
            >
              {(!expansion || expansion.status === 'loading') && (
                <Typography data-testid={`operation-expansion-loading-${row.jobReferenceId}`} variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
              )}
              {expansion?.status === 'error' && (
                <Stack direction='row' spacing={1} alignItems='center' data-testid={`operation-expansion-error-${row.jobReferenceId}`}>
                  <Typography variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the operation detail')}</Typography>
                  <Button size='small' onClick={() => onRetry(row.jobReferenceId)} sx={{ textTransform: 'none' }}>{t_('Retry')}</Button>
                </Stack>
              )}
              {/* N4 and the planned data come by separate doors: if N4 fails and the planned
                  data lands, the tabs still show, with N4's part empty. */}
              {(expansion?.status === 'ready' || (expansion?.status === 'error' && theoretical?.status === 'ready')) && (
                <OperationExpansionDetails data={expansion.data} row={row} theoretical={theoretical} />
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  )
})

export default OperationRow
